import React, { useState } from "react";
import { Spinner, Table, Collapse, Button, Popover, PopoverBody } from "reactstrap";
import PropTypes from "prop-types";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";
import { getCoreParkTicketsReports } from "helpers/fakebackend_helper";

const CoreParkReportTable = ({ locations }) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [expanded, setExpanded] = useState({});
    const [page, setPage] = useState({});
    const [loadingTickets, setLoadingTickets] = useState({});
    const [ticketData, setTicketData] = useState({});
    const [squarePopoverOpen, setSquarePopoverOpen] = useState(false);
    const [squareTransactionsOpen, setSquareTransactionsOpen] = useState(false);
    const [netSalesOpen, setNetSalesOpen] = useState(false);
    const itemsPerPage = 5;

    const handleViewTickets = async (locId, start_date, end_date) => {
        const isOpen = expanded[locId];
        setExpanded((prev) => ({ ...prev, [locId]: !isOpen }));

        if (!isOpen && !ticketData[locId]) {
            setLoadingTickets((prev) => ({ ...prev, [locId]: true }));
            try {
                const adjustedEndDate = new Date(end_date);
                adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);

                const payload = {
                    location_id: locId,
                    start_date,
                    end_date: adjustedEndDate.toISOString().split('T')[0]
                };

                const response = await getCoreParkTicketsReports(user.token, payload);
                setTicketData((prev) => ({
                    ...prev,
                    [locId]: response.data.tickets || [],
                }));
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingTickets((prev) => ({ ...prev, [locId]: false }));
            }
        }
    };

    return (
        <div className="overflow-x-auto">
            <Table bordered responsive className="align-middle table-nowrap">
                <thead className="table-light">
                    <tr>
                        <th>Location</th>
                        <th>Total CP Tickets</th>
                        <th id="squareTransactionsTH" style={{ cursor: "help" }}>
                            SQ Transactions
                        </th>
                        <Popover
                            placement="top"
                            isOpen={squareTransactionsOpen}
                            target="squareTransactionsTH"
                            toggle={() => setSquareTransactionsOpen(!squareTransactionsOpen)}
                        >
                            <PopoverBody>
                                All the transactions including Events in Square.
                            </PopoverBody>
                        </Popover>
                        <th>CP Cash</th>
                        <th id="squareCardTH" style={{ cursor: "help" }}>
                            SQ Card
                        </th>
                        <Popover
                            placement="top"
                            isOpen={squarePopoverOpen}
                            target="squareCardTH"
                            toggle={() => setSquarePopoverOpen(!squarePopoverOpen)}
                        >
                            <PopoverBody>
                                Card Transactions - Transaction Fees from Square Directly.
                            </PopoverBody>
                        </Popover>
                        <th>CP + SQ Total Parking</th>
                        <th>SQ Tip</th>
                        <th>SQ Trx Fee</th>
                        <th>SQ Card Tax</th>
                        <th>SQ Refund Card Tax</th>
                        <th>SQ Prcs. Fee</th>
                        <th>SQ Refund & Return</th>
                        <th>SQ Refund Trx Fee</th>
                        <th id="netSalesTH" style={{ cursor: "help" }}>
                            Net Sales
                        </th>
                        <Popover
                            placement="top"
                            isOpen={netSalesOpen}
                            target="netSalesTH"
                            toggle={() => setNetSalesOpen(!netSalesOpen)}
                        >
                            <PopoverBody>
                                CP Cash + SQ Card + SQ Tips + SQ Trx Fee + CP Tax - SQ Processing Fee - SQ Refunds - SQ Refunds Trx Fee - SQ Refund Card Tax
                            </PopoverBody>
                        </Popover>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {locations
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter((item) => item.corepark.tickets > 0)
                        .map((loc) => {
                            const totals = loc.corepark;
                            const squareTotals = loc.square || {
                                processing_fee_total: 0,
                                square_total: 0,
                                refunds_total: 0,
                                total_trx_fee_amount: 0,
                                total_card_tax_amount: 0,
                                total_refund_card_tax_amount: 0,
                                total_refund_fee_amount: 0,
                                square_tips: 0,
                                transactions: 0
                            };

                            const currentPage = page[loc.id] || 1;

                            const totalSquareCard =
                                squareTotals.square_total -
                                squareTotals.total_trx_fee_amount -
                                squareTotals.total_card_tax_amount
                                // squareTotals.total_refund_card_tax_amount;

                            return (
                                <React.Fragment key={loc.id}>
                                    <tr>
                                        <td>{loc.name}</td>
                                        <td>{loc.corepark.tickets}</td>
                                        <td>{squareTotals.transactions}</td>
                                        <td>${formatCurrency(totals.cash)}</td>
                                        <td>${formatCurrency(totalSquareCard)}</td>
                                        <td>${formatCurrency(totals.cash + totalSquareCard)}</td>
                                        <td>${formatCurrency(squareTotals.square_tips)}</td>
                                        <td>${formatCurrency(squareTotals.total_trx_fee_amount)}</td>
                                        <td>${formatCurrency(squareTotals.total_card_tax_amount)}</td>
                                        <td>${formatCurrency(squareTotals.total_refund_card_tax_amount)}</td>
                                        <td>${formatCurrency(squareTotals.processing_fee_total / 100)}</td>
                                        <td>${formatCurrency(squareTotals.refunds_total - squareTotals.total_refund_fee_amount)}</td>
                                        <td>${formatCurrency(squareTotals.total_refund_fee_amount)}</td>

                                        <td>
                                            ${formatCurrency(
                                                totals.cash +
                                                totalSquareCard +
                                                squareTotals.square_tips +
                                                squareTotals.total_trx_fee_amount +
                                                squareTotals.total_card_tax_amount -
                                                squareTotals.total_refund_card_tax_amount -
                                                (squareTotals.processing_fee_total / 100) -
                                                squareTotals.refunds_total -
                                                squareTotals.total_refund_fee_amount
                                            )}
                                        </td>
                                        <td>
                                            <Button
                                                className="button-gradient-bg"
                                                onClick={() => handleViewTickets(loc.id, loc.start_date, loc.end_date)
                                                }
                                            >
                                                {expanded[loc.id] ? "Hide Tickets" : "View Tickets"}
                                            </Button>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colSpan={13} className="p-0 border-0">
                                            <Collapse isOpen={expanded[loc.id]}>
                                                {loadingTickets[loc.id] ? (
                                                    <div className="text-center py-3">
                                                        <Spinner color="primary" />
                                                    </div>
                                                ) : (
                                                    <Table
                                                        bordered
                                                        size="sm"
                                                        responsive
                                                        className="mb-0 table-striped"
                                                    >
                                                        <thead>
                                                            <tr>
                                                                <th>Ticket ID</th>
                                                                <th>Checkin</th>
                                                                <th>Checkout</th>
                                                                <th>Price</th>
                                                                <th>Cash</th>
                                                                <th>Card</th>
                                                                <th>Tax</th>
                                                                <th>Trx Fee</th>
                                                                <th>Tip</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(() => {
                                                                const allTickets = ticketData[loc.id] || [];
                                                                const totalPagesCount = Math.max(
                                                                    1,
                                                                    Math.ceil(allTickets.length / itemsPerPage)
                                                                );
                                                                const start =
                                                                    (currentPage - 1) * itemsPerPage;
                                                                const paginatedTickets = allTickets.slice(
                                                                    start,
                                                                    start + itemsPerPage
                                                                );

                                                                return (
                                                                    <>
                                                                        {paginatedTickets.map((t) => (
                                                                            <tr key={t.id}>
                                                                                <td>{t.cp_ticket_id}</td>
                                                                                <td>
                                                                                    {formatDate(
                                                                                        t.cp_checkin_date
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    {formatDate(
                                                                                        t.cp_checkout_date
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    $
                                                                                    {t.ticket_price?.toFixed(
                                                                                        2
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    $
                                                                                    {t.ticket_cash_collected?.toFixed(
                                                                                        2
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    $
                                                                                    {t.ticket_card_collected?.toFixed(
                                                                                        2
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    $
                                                                                    {t.ticket_tax_amount?.toFixed(
                                                                                        2
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    $
                                                                                    {t.ticket_transaction_fee_amount?.toFixed(
                                                                                        2
                                                                                    )}
                                                                                </td>
                                                                                <td>
                                                                                    $
                                                                                    {t.ticket_tip_amount?.toFixed(
                                                                                        2
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                        {allTickets.length === 0 && (
                                                                            <tr>
                                                                                <td
                                                                                    colSpan="9"
                                                                                    className="text-center py-3"
                                                                                >
                                                                                    No tickets found
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                        {allTickets.length > itemsPerPage && (
                                                                            <tr>
                                                                                <td
                                                                                    colSpan="9"
                                                                                    className="p-2"
                                                                                >
                                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                                        <Button
                                                                                            color="secondary"
                                                                                            size="sm"
                                                                                            onClick={() =>
                                                                                                setPage(
                                                                                                    (
                                                                                                        prev
                                                                                                    ) => ({
                                                                                                        ...prev,
                                                                                                        [loc.id]:
                                                                                                            Math.max(
                                                                                                                1,
                                                                                                                currentPage -
                                                                                                                1
                                                                                                            ),
                                                                                                    })
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                currentPage ===
                                                                                                1
                                                                                            }
                                                                                        >
                                                                                            Previous
                                                                                        </Button>
                                                                                        <span>
                                                                                            Page{" "}
                                                                                            {currentPage} of{" "}
                                                                                            {
                                                                                                totalPagesCount
                                                                                            }
                                                                                        </span>
                                                                                        <Button
                                                                                            color="secondary"
                                                                                            size="sm"
                                                                                            onClick={() =>
                                                                                                setPage(
                                                                                                    (
                                                                                                        prev
                                                                                                    ) => ({
                                                                                                        ...prev,
                                                                                                        [loc.id]:
                                                                                                            Math.min(
                                                                                                                totalPagesCount,
                                                                                                                currentPage +
                                                                                                                1
                                                                                                            ),
                                                                                                    })
                                                                                                )
                                                                                            }
                                                                                            disabled={
                                                                                                currentPage ===
                                                                                                totalPagesCount
                                                                                            }
                                                                                        >
                                                                                            Next
                                                                                        </Button>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </tbody>
                                                    </Table>
                                                )}
                                            </Collapse>
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                    {locations.length === 0 && (
                        <tr>
                            <td colSpan={13} className="text-center py-3">
                                No locations found
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    );
};

CoreParkReportTable.propTypes = {
    locations: PropTypes.array.isRequired,
};

export default CoreParkReportTable;